import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import styled from "styled-components";
import InputBox from "../Generic/InputBox";
import Checkbox from "../Generic/Checkbox";
import Dropdown from "../Generic/Dropdown";
import { beautifyKey, deepCopy, isEmpty } from "../util/util";
import Button from "../Generic/Button";
import { errorNotification } from "../Generic/Notifications";
import LandingConnector from "../Landing/Connector/LandingConnector";
import { useNavigate, useParams } from "react-router-dom";
import InventoryPlanService from "./Services/InventoryPlanService";
import { customDataTableStyles } from "./Styles/DataTableStyles";
import { DEFAULT_QUANTITY_UNIT } from "../config/app.config";

const Page = styled.div`
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	padding: 2rem;
	gap: 2rem;
	overflow-y: auto;
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	font-weight: 600;
`;

const Block = styled.div`
	width: max-content;
	height: auto;
	display: flex;
	gap: ${(props) => props.gap || "1rem"};
	align-items: center;
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	gap: ${(props) => props.gap};
	overflow-y: ${(props) => props.overflowY};
	overflow-x: ${(props) => props.overflowX};
	height: ${(props) => props.height};
	max-height: ${(props) => props.maxHeight};
	min-height: ${(props) => props.minHeight};
	max-width: ${(props) => props.maxWidth};
	white-space: ${(props) => props.whiteSpace};
	border: ${(props) => props.border};
	border-radius: ${(props) => props.borderRadius};
	flex-wrap: ${(props) => props.flexWrap};
	@media (max-width: 1000px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const MODULE_TYPES = {
	DISPENSER: 1,
	PLATE_RACKS: 2,
};

const DISPENSER_TYPES = {
	CONVEYOR: 20,
	PUMP: 30,
};

const TABS = {
	CONVEYOR_DISPENSERS: "CONVEYOR_DISPENSERS",
	PUMP_DISPENSERS: "PUMP_DISPENSERS",
	EPRS: "EPRS",
};

function generateTableColumns(tab, { }) {
	switch (tab) {
		case TABS.CONVEYOR_DISPENSERS:
			return [
				{
					name: "Symbol",
					cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
					grow: 1,
					center: true
				},
				{
					name: "Mapping",
					cell: (data) => <img height={"80px"} src={data.mappingImage} alt="dispenser-mapping" />,
					grow: 1,
					center: true
				},
				{
					name: "Ingredient",
					selector: (data) => data.name,
					allowOverflow: true,
					center: true,
				},
				{
					name: "Amount to be filled",
					cell: (data) => <span>
						{data.amountToBeFilled ?? 0} {DEFAULT_QUANTITY_UNIT}
					</span>,
					center: true,
				},
				{
					name: "Container Type",
					selector: (data) => data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.containerType,
					center: true,
				},
				{
					name: "Base Type",
					selector: (data) => data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.baseType,
					center: true,
				},
				{
					name: "Spout Type",
					selector: (data) => data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.spoutType,
					center: true,
				},
			];

		case TABS.PUMP_DISPENSERS:
			return [
				{
					name: "Symbol",
					cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
					grow: 1,
					center: true
				},
				{
					name: "Mapping",
					cell: (data) => <img height={"80px"} src={data.mappingImage} alt="dispenser-mapping" />,
					grow: 1,
					center: true
				},
				{
					name: "Ingredient",
					selector: (data) => data.name,
					allowOverflow: true,
					center: true,
				},
				{
					name: "Amount to be filled",
					cell: (data) => <span>
						{data.amountToBeFilled ?? 0} {DEFAULT_QUANTITY_UNIT}
					</span>,
					center: true,
				},
				{
					name: "Agitator",
					cell: (data) => {
						if (!data.itemId) return;

						const defaultValue = data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.agitator;
						if (typeof defaultValue !== 'undefined') {
							if (typeof defaultValue === "object") {
								return JSON.stringify(defaultValue);
							} else {
								return defaultValue === true ? "Yes" : "No";
							}
						}
					},
					center: true,
				},
			];

		case TABS.EPRS:
			return [
				{
					name: "Symbol",
					cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
					center: true
				},
				// {
				// 	name: "Module Shape",
				// 	cell: (data) => <img height={"80px"} src={data.moduleView} alt="module-symbol" />,
				// 	grow: 2,
					
				// },
				{
					name: "Ingredient",
					selector: (data) => data.name,
					allowOverflow: true,
					center: true,
				},
				{
					name: "Amount to be filled",
					selector: (data) => data.amountToBeFilled,
					center: true,
				},
			];

		default:
			return [];
	}
}

const InventoryPlanDetails = (props) => {
	const { machineID } = props;

	const { id } = useParams();

	const [loading, setLoading] = useState(true);
	const [lookupData, setLookupData] = useState({});
	const [tableData, setTableData] = useState({});
	const [activeTab, setActiveTab] = useState(TABS.CONVEYOR_DISPENSERS);

	const navigate = useNavigate();

	function prepareDataTablePayload(data) {
		let tableData = {},
			conveyorDispensers = [],
			pumpDispensers = [],
			eprs = [], eprGroups = [];
		const { modules:inventoryData } = data;
		for (const moduleId in inventoryData) {
			const moduleData = inventoryData[moduleId];
			const visualPriority = moduleData?.visualPriority.length ? moduleData?.visualPriority : Object.keys(moduleData.systems || {});
			for (const systemId of visualPriority) {
				let recordData = {
					moduleSymbol: moduleData.moduleSymbol,
					moduleId: moduleId,
					systemId: systemId,
					moduleType: moduleData.moduleType,
					layer: moduleData.layer,
					...moduleData.systems[systemId]
				};
				switch (moduleData.moduleType) {
					case MODULE_TYPES.DISPENSER:
						const dispenserType = moduleData.systems[systemId]?.systemType;
						if (dispenserType === DISPENSER_TYPES.CONVEYOR) {
							conveyorDispensers.push(recordData);
						} else if (dispenserType === DISPENSER_TYPES.PUMP) {
							pumpDispensers.push(recordData);
						}
						break;

					case MODULE_TYPES.PLATE_RACKS:
						const eprGroupIndex = eprGroups.findIndex(item => item.moduleId === moduleId); 
						if (eprGroups[eprGroupIndex]) {
							eprGroups[eprGroupIndex].amountToBeFilled += moduleData.systems[systemId].amountToBeFilled;
						} else {
							eprGroups.push(recordData)
						}
						break;

					default:
						break;
				}
			}
		}

		tableData = {
			[TABS.CONVEYOR_DISPENSERS]: conveyorDispensers,
			[TABS.PUMP_DISPENSERS]: pumpDispensers,
			[TABS.EPRS]: eprGroups,
		};

		return tableData;
	}

	async function getPlanData(id) {
		setLoading(true);
		const { data, error } = await InventoryPlanService.getInventoryPlan(id);
		if (data) {
			const { plan } = data;
			const dataTablePayload = prepareDataTablePayload(plan);
			setTableData(dataTablePayload);
			setLookupData(data);
		}
		if(error) {
			errorNotification(error);
		}
		setLoading(false);
	}

	function renderActiveTab(activeTab, helperFunctions, tableData, loading) {
		const columns = generateTableColumns(activeTab, helperFunctions);
		const data = tableData?.[activeTab];
		let renderData = [];

		renderData.push(
			<DataTable progressPending={loading} customStyles={customDataTableStyles} key={activeTab} responsive={true} style={{ height: "60%" }} striped fixedHeader columns={columns} data={data} />
		);

		return renderData;
	}
	
	useEffect(() => {
		getPlanData(id);
	}, [])

	return (
		<Page>
			<Block>
				<span onClick={() => navigate(`/${machineID}/inventoryPlan/list/`)} className="material-symbols-outlined" style={{ cursor: "pointer" }}>
					arrow_back
				</span>
				<Header>{lookupData.name || id}</Header>
				{/* <Button onClick={() => navigate(`/${machineID}/inventoryPlan/edit/${id}`)} >
					Edit
				</Button> */}
			</Block>


			<Block>
				<Button active={activeTab === TABS.CONVEYOR_DISPENSERS} onClick={() => setActiveTab(TABS.CONVEYOR_DISPENSERS)}>
					Solid Ingredients
				</Button>
				<Button active={activeTab === TABS.PUMP_DISPENSERS} onClick={() => setActiveTab(TABS.PUMP_DISPENSERS)}>
					Liquid Ingredients
				</Button>
				<Button active={activeTab === TABS.EPRS} onClick={() => setActiveTab(TABS.EPRS)}>
					Bowl Racks
				</Button>
			</Block>

			{renderActiveTab(activeTab, {} , tableData, loading)}

		</Page>
	);
};

export default LandingConnector(InventoryPlanDetails);
